import React, { useRef } from 'react'
import { PinInput, normalizePinCode } from '@/components/Inputs/PinInput'
import { useForm, useWatch } from 'react-hook-form'
import { useAuth } from '@/hooks/auth/useAuth'
import { Button } from '@/components/Buttons/Button'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { useTranslation } from '@/localization/useTranslation'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { AuthErrorType } from '@/actors/AuthActor/types'
import { Group } from '@/components/Group'
import { PAIR_CODE_LENGTH, PAIR_ERROR_BODIES } from './constants'

interface PairProps {
  pairCode?: string
  onPaired: () => void
}

export const Pair: React.FC<PairProps> = (props) => {
  const { pairCode, onPaired } = props
  const { t } = useTranslation()
  const formTitle = t('Enter the code to activate your device')
  const { pairMutation } = useAuth()
  const { isPending, isSuccess, error, reset } = pairMutation
  const {
    control,
    handleSubmit,
  } = useForm({
    defaultValues: {
      pairCode: normalizePinCode(pairCode ?? '', PAIR_CODE_LENGTH)
    }
  })
  const pairCodeValue = useWatch({ control, name: 'pairCode' })
  const isSubmitBlocked = pairCodeValue.length !== PAIR_CODE_LENGTH || isPending || isSuccess || !!error

  // A second submit dispatched before the re-render still sees the previous
  // render's isPending, so render-time state alone lets it through (PRO-957).
  // Never released on success: resends land right after a 200 while this form
  // stays mounted under the success modal.
  const isSubmittingRef = useRef(false)

  const onSubmit = (data: { pairCode: string }) => {
    if (isSubmittingRef.current || data.pairCode.length !== PAIR_CODE_LENGTH) return
    isSubmittingRef.current = true
    pairMutation.mutate(data, { onSuccess: onPaired })
  }

  const handleError = () => {
    isSubmittingRef.current = false
    reset()
  }

  return (
    <>
      <LoadingOverlay
        isLoading={isPending}
      />
      <AuthErrorModal
        error={error as unknown as AuthErrorType}
        handleError={handleError}
        bodyByCode={PAIR_ERROR_BODIES}
      />
      <h2>{formTitle}</h2>
      {/* Deferred so the ref-reading onSubmit is not bound during render (react-hooks/refs). */}
      <form action="#" onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Group align="center" justify="center">
          <PinInput
            pinLength={PAIR_CODE_LENGTH}
            control={control}
            name="pairCode"
          />
        </Group>
        <Button
          fullWidth
          type="submit"
          disabled={isSubmitBlocked}
        >
          {t('Activate')}
        </Button>
      </form>
    </>
  )
}
